import { BLEND_MODES, Container, MIPMAP_MODES, Sprite, Texture } from "pixi.js"
import { stageViewport } from "../../../../canvas_layout"
import { findRendererApp } from "../renderer_app"
import { softGlowTexture, sparkTexture } from "../gui/ember_atmosphere"
import { choreoMs, realMs } from "./intro_pace"
import { BackdropEllipse, LOADING_BACKDROP_STOPS } from "./loading_handoff"
import { Pen, paintCloud, paintShip, paintStatic, skyGradient } from "./shipwreck_painter"
import {
    Bounds,
    CLOUD,
    Dash,
    GlitterRow,
    HORIZON,
    Land,
    MIRROR_AXIS,
    MOON,
    REST,
    SCENE,
    SHIP_ON_RIGHT,
    STATIC_DONE,
    Star,
    buildGlitter,
    buildLand,
    buildStars,
    buildWater,
    easeInOutCubic,
    lanternAt,
    mirrorX,
    prog,
    shakeAt,
    smooth,
} from "./shipwreck_geometry"

/**
 * The login screen's backdrop: a ship drawn in line art, sailing in out of the night and running
 * aground beside the card. Replaces the forest picture, the nightfall wash, the fog and the embers.
 *
 * Built from three kinds of piece, each for its cost:
 *  - Line art is painted with Canvas2D (antialiased; the game's renderer is not) into canvases that
 *    pixi shows as textures. The sky, horizon, moon and far shore are one layer, repainted only while
 *    they are being drawn in and then never again. The ship -- with the rocks, the spray and the swell
 *    around it -- is a small layer repainted every frame.
 *  - The water's glints and the stars are plain sprites off one tiny texture: hundreds of them cost
 *    nothing, and they never need a repaint.
 *  - Light -- the moon's halo, the mist on the horizon, the lantern and its reflection -- is additive
 *    sprites, never a filter (see intro_theme on why the login screen uses no filters).
 *
 * The whole thing is a function of time since the loading screen handed over (see intro_pace.ts),
 * driven by its own animation frame loop, so a background tab lands on the right frame.
 */

const INK_TINT = 0xe9dec6
const LANTERN_TINT = 0xffb860
const REFLECTION_TINT = 0xffbe6e
const MOON_TINT = 0xbecde6
const MIST_TINT = 0x96aac8

interface Region {
    x: number
    y: number
    w: number
    h: number
}

/** Everything the ship touches, in authored coordinates: the far pose, the masts, the swell, the spray. */
const SHIP_REGION: Region = { x: -70, y: 170, w: 330, h: 258 }
const CLOUD_REGION: Region = { x: CLOUD.x - 82, y: CLOUD.y - 16, w: 164, h: 32 }

/** Line art is drawn at the screen's density, capped so the uploads stay small on a big display. */
const STATIC_MAX_DENSITY = 2
const LIVE_MAX_DENSITY = 2.5

/** Below this the warm loading backdrop has faded and is freed. */
const BACKDROP_DENSITY = 0.5

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v))

/** A Canvas2D surface shown as a pixi sprite. `place` sizes it; `pen` paints into it; `commit` uploads. */
class CanvasLayer {
    readonly sprite: Sprite
    private readonly canvas: HTMLCanvasElement
    private readonly ctx: CanvasRenderingContext2D | null
    private readonly texture: Texture
    private sky?: CanvasGradient
    private region: Region = { x: 0, y: 0, w: 1, h: 1 }
    private density = 1

    constructor() {
        this.canvas = document.createElement("canvas")
        this.canvas.width = 1
        this.canvas.height = 1
        this.ctx = this.canvas.getContext("2d")
        this.texture = Texture.from(this.canvas)
        this.texture.baseTexture.mipmap = MIPMAP_MODES.OFF
        this.sprite = new Sprite(this.texture)
    }

    /** `region` is in authored coordinates; the sprite goes wherever the mirror puts it. */
    place = (region: Region, density: number) => {
        this.region = region
        this.density = density
        const w = Math.max(1, Math.ceil(region.w * density))
        const h = Math.max(1, Math.ceil(region.h * density))
        if (this.canvas.width !== w || this.canvas.height !== h) {
            this.canvas.width = w
            this.canvas.height = h
        }
        this.sprite.position.set(this.outX(), region.y)
        this.sprite.scale.set(1 / density)
    }

    /** A pen in authored coordinates. Cleared first unless the painter covers every pixel itself. */
    pen = (clear: boolean): Pen | undefined => {
        const ctx = this.ctx
        if (!ctx) {
            return undefined
        }
        ctx.setTransform(1, 0, 0, 1, 0, 0)
        if (clear) {
            ctx.clearRect(0, 0, this.canvas.width, this.canvas.height)
        }
        const d = this.density
        ctx.setTransform(d, 0, 0, d, -this.outX() * d, -this.region.y * d)
        if (SHIP_ON_RIGHT) {
            ctx.translate(2 * MIRROR_AXIS, 0)
            ctx.scale(-1, 1)
        }
        this.sky = this.sky || skyGradient(ctx)
        return { ctx, sky: this.sky }
    }

    /**
     * Upload what was painted. Through the resource, not `baseTexture.update()`: that only marks the
     * texture dirty and never re-reads the canvas size, so a canvas resized after `Texture.from`
     * stays a 1x1 texture -- the line art silently vanishes while every sprite still draws.
     */
    commit = () => this.texture.update()

    dispose = () => {
        this.sprite.destroy()
        this.texture.destroy(true)
    }

    private outX = () => (SHIP_ON_RIGHT ? 2 * MIRROR_AXIS - (this.region.x + this.region.w) : this.region.x)
}

/**
 * One short white stroke with round ends, for every glint on the water. 32x8 with the stroke from
 * 3 to 29 and 2px thick: `dashSprite` sizes it by visible length and thickness.
 */
let dash: Texture | undefined
const dashTexture = (): Texture => {
    if (dash) {
        return dash
    }
    try {
        const canvas = document.createElement("canvas")
        canvas.width = 32
        canvas.height = 8
        const g = canvas.getContext("2d")
        if (!g) {
            return Texture.WHITE
        }
        g.strokeStyle = "#ffffff"
        g.lineWidth = 2
        g.lineCap = "round"
        g.beginPath()
        g.moveTo(4, 4)
        g.lineTo(28, 4)
        g.stroke()
        dash = Texture.from(canvas)
    } catch (e) {
        dash = Texture.WHITE
    }
    return dash
}

const dashSprite = (tint: number, additive: boolean = false): Sprite => {
    const sprite = new Sprite(dashTexture())
    sprite.anchor.set(0.5)
    sprite.tint = tint
    if (additive) {
        sprite.blendMode = BLEND_MODES.ADD
    }
    return sprite
}

/** Lay a dash sprite out by its visible length and stroke width, centred on (x, y), in authored x. */
const setDash = (sprite: Sprite, x: number, y: number, length: number, thickness: number) => {
    sprite.position.set(mirrorX(x), y)
    sprite.width = (Math.max(0.5, length) * 32) / 26
    sprite.height = thickness * 4
}

export interface ShipwreckSceneOptions {
    /** The loading screen's backdrop, when the scene takes over from it: drawn here, then faded. */
    backdrop?: BackdropEllipse
    /** Start with everything drawn and the ship already aground (prefers-reduced-motion). */
    atRest?: boolean
}

export class ShipwreckScene extends Container {
    /** Everything but the loading backdrop: this is what shakes when the ship strikes. */
    private readonly world = new Container()
    private readonly staticLayer = new CanvasLayer()
    private readonly cloudLayer = new CanvasLayer()
    private readonly shipLayer = new CanvasLayer()
    private readonly halo: Sprite
    private readonly mist: Sprite
    private readonly starLayer = new Container()
    private readonly waterLayer = new Container()
    private readonly lanternGlow: Sprite
    private readonly reflections: Sprite[] = []
    private readonly backdrop?: BackdropEllipse
    private backdropSprite?: Sprite

    private bounds: Bounds = { left: 0, right: 800, top: 0, bottom: 460 }
    private land: Land = buildLand(800)
    private stars: { star: Star; dot: Sprite; arms: Sprite[] }[] = []
    private water: { dash: Dash; sprite: Sprite }[] = []
    private glitter: { row: GlitterRow; sprites: Sprite[] }[] = []

    private staticDirty = true
    private cloudDirty = true
    private frameCount = 0
    private readonly startedAt: number
    private frame?: number
    private disposed = false

    constructor({ backdrop, atRest = false }: ShipwreckSceneOptions = {}) {
        super()
        this.backdrop = backdrop
        this.startedAt = Date.now() - (atRest ? realMs(REST) : 0)

        this.halo = new Sprite(softGlowTexture())
        this.halo.anchor.set(0.5)
        this.halo.tint = MOON_TINT
        this.halo.blendMode = BLEND_MODES.ADD

        this.mist = new Sprite(softGlowTexture())
        this.mist.anchor.set(0.5)
        this.mist.tint = MIST_TINT
        this.mist.blendMode = BLEND_MODES.ADD

        this.lanternGlow = new Sprite(softGlowTexture())
        this.lanternGlow.anchor.set(0.5)
        this.lanternGlow.tint = LANTERN_TINT
        this.lanternGlow.blendMode = BLEND_MODES.ADD

        for (let i = 0; i < 5; i++) {
            this.reflections.push(dashSprite(REFLECTION_TINT, true))
        }

        this.world.addChild(
            this.staticLayer.sprite,
            this.halo,
            this.mist,
            this.starLayer,
            this.cloudLayer.sprite,
            this.waterLayer,
            this.shipLayer.sprite,
            this.lanternGlow,
            ...this.reflections,
        )
        this.addChild(this.world)

        this.resize()
        this.tick()
    }

    /** Fit the scene to the visible stage: wider and taller than 800 x 460 on an expanded phone. */
    resize = () => {
        if (this.disposed) {
            return
        }
        const v = stageViewport()
        this.bounds = { left: v.left, right: v.right, top: v.top, bottom: v.bottom }
        this.land = buildLand(v.right)

        const resolution = findRendererApp()?.renderer?.resolution ?? 1
        this.staticLayer.place({ x: v.left, y: v.top, w: v.width, h: v.height }, clamp(resolution, 1, STATIC_MAX_DENSITY))
        this.cloudLayer.place(CLOUD_REGION, clamp(resolution, 1, LIVE_MAX_DENSITY))
        this.shipLayer.place(SHIP_REGION, clamp(resolution, 1, LIVE_MAX_DENSITY))

        this.buildStars()
        this.buildWater()

        this.halo.position.set(mirrorX(MOON.x), MOON.y)
        this.halo.width = this.halo.height = MOON.r * 11
        this.mist.position.set(MIRROR_AXIS, HORIZON + 2)
        this.mist.width = v.width * 1.3
        this.mist.height = 70

        this.bakeBackdrop()
        this.staticDirty = true
        this.cloudDirty = true
        this.paint(this.now())
    }

    dispose = () => {
        this.disposed = true
        if (this.frame !== undefined && typeof cancelAnimationFrame === "function") {
            cancelAnimationFrame(this.frame)
        }
        this.frame = undefined
        this.dropBackdrop()
        this.staticLayer.dispose()
        this.cloudLayer.dispose()
        this.shipLayer.dispose()
        // The glow and dash textures are shared and cached; only the sprites go.
        this.removeChildren()
        this.world.destroy({ children: true })
    }

    private now = () => choreoMs(Date.now() - this.startedAt)

    private tick = () => {
        if (this.disposed) {
            return
        }
        this.paint(this.now())
        if (typeof requestAnimationFrame === "function") {
            this.frame = requestAnimationFrame(this.tick)
        }
    }

    private buildStars = () => {
        this.stars.forEach(s => [s.dot, ...s.arms].forEach(sprite => sprite.destroy()))
        this.starLayer.removeChildren()
        this.stars = buildStars(this.bounds).map(star => {
            const dot = new Sprite(sparkTexture())
            dot.anchor.set(0.5)
            dot.tint = INK_TINT
            dot.position.set(mirrorX(star.x), star.y)
            dot.width = dot.height = star.r * 7
            const arms = star.cross ? [dashSprite(INK_TINT), dashSprite(INK_TINT)] : []
            arms.forEach((arm, i) => {
                setDash(arm, star.x, star.y, 5.2, 0.5)
                arm.rotation = i * (Math.PI / 2)
            })
            this.starLayer.addChild(dot, ...arms)
            return { star, dot, arms }
        })
    }

    private buildWater = () => {
        this.waterLayer.children.slice().forEach(child => child.destroy())
        this.waterLayer.removeChildren()
        this.water = buildWater(this.bounds).map(d => {
            const sprite = dashSprite(INK_TINT)
            this.waterLayer.addChild(sprite)
            return { dash: d, sprite }
        })
        this.glitter = buildGlitter(this.bounds).map(row => {
            const sprites = [dashSprite(INK_TINT), dashSprite(INK_TINT)]
            this.waterLayer.addChild(...sprites)
            return { row, sprites }
        })
    }

    /**
     * The loading screen's own backdrop, repainted in pixi under the HTML one so the HTML layer can
     * drop away without the screen changing. It then fades to show the night behind it.
     */
    private bakeBackdrop = () => {
        const e = this.backdrop
        if (!e || this.now() >= SCENE.backdrop[1]) {
            this.dropBackdrop()
            return
        }
        const b = this.bounds
        try {
            const canvas = document.createElement("canvas")
            canvas.width = Math.max(1, Math.ceil((b.right - b.left) * BACKDROP_DENSITY))
            canvas.height = Math.max(1, Math.ceil((b.bottom - b.top) * BACKDROP_DENSITY))
            const g = canvas.getContext("2d")
            if (!g) {
                return
            }
            g.fillStyle = LOADING_BACKDROP_STOPS[LOADING_BACKDROP_STOPS.length - 1][1]
            g.fillRect(0, 0, canvas.width, canvas.height)
            g.setTransform(BACKDROP_DENSITY, 0, 0, BACKDROP_DENSITY, -b.left * BACKDROP_DENSITY, -b.top * BACKDROP_DENSITY)
            g.translate(e.cx, e.cy)
            g.scale(1, e.ry / e.rx)
            const gradient = g.createRadialGradient(0, 0, 0, 0, 0, e.rx)
            LOADING_BACKDROP_STOPS.forEach(([offset, color]) => gradient.addColorStop(offset, color))
            g.fillStyle = gradient
            g.fillRect(-e.rx, -e.rx, e.rx * 2, e.rx * 2)

            this.dropBackdrop()
            const sprite = new Sprite(Texture.from(canvas))
            sprite.position.set(b.left, b.top)
            sprite.scale.set(1 / BACKDROP_DENSITY)
            this.backdropSprite = sprite
            this.addChild(sprite)
        } catch (err) {
            // No canvas: no backdrop. The HTML one fades over the scene instead.
        }
    }

    private dropBackdrop = () => {
        const sprite = this.backdropSprite
        if (!sprite) {
            return
        }
        this.backdropSprite = undefined
        sprite.parent?.removeChild(sprite)
        sprite.destroy({ texture: true, baseTexture: true })
    }

    private paint = (t: number) => {
        if (this.backdropSprite) {
            const alpha = 1 - easeInOutCubic(prog(t, SCENE.backdrop[0], SCENE.backdrop[1]))
            if (alpha <= 0) {
                this.dropBackdrop()
            } else {
                this.backdropSprite.alpha = alpha
            }
        }

        this.paintStatic(t)
        this.paintCloud(t)
        this.paintLights(t)
        this.paintWater(t)
        this.paintShip(t)

        const [sx, sy] = shakeAt(t)
        this.world.position.set(SHIP_ON_RIGHT ? -sx : sx, sy)
        this.frameCount++
    }

    /** Repainted at half the frame rate while it is being drawn in, then never again. */
    private paintStatic = (t: number) => {
        const drawing = t < STATIC_DONE + 100
        if (!this.staticDirty && (!drawing || this.frameCount % 2 === 1)) {
            return
        }
        const pen = this.staticLayer.pen(false)
        if (pen) {
            paintStatic(pen, t, this.land, this.bounds)
            this.staticLayer.commit()
        }
        this.staticDirty = false
    }

    private paintCloud = (t: number) => {
        const offset = CLOUD.drift * Math.sin(t / CLOUD.period)
        const base = SHIP_ON_RIGHT ? 2 * MIRROR_AXIS - (CLOUD_REGION.x + CLOUD_REGION.w) : CLOUD_REGION.x
        this.cloudLayer.sprite.x = base + (SHIP_ON_RIGHT ? -offset : offset)
        if (!this.cloudDirty && t > SCENE.cloud[1] + 100) {
            return
        }
        const pen = this.cloudLayer.pen(true)
        if (pen) {
            paintCloud(pen, t)
            this.cloudLayer.commit()
        }
        this.cloudDirty = false
    }

    private paintLights = (t: number) => {
        this.halo.alpha = 0.25 * smooth(prog(t, SCENE.moon[0] + 400, SCENE.moon[1] + 500))
        this.mist.alpha = 0.12 * smooth(prog(t, SCENE.horizon[0], SCENE.horizon[1] + 800)) * (0.85 + 0.15 * Math.sin(t / 4100))

        this.stars.forEach(({ star, dot, arms }, i) => {
            const appear = prog(t, SCENE.stars + i * 70, SCENE.stars + i * 70 + 500)
            const alpha = appear * (0.28 + 0.42 * (0.5 + 0.5 * Math.sin(t / star.per + star.ph)))
            dot.alpha = alpha
            arms.forEach(arm => (arm.alpha = alpha * 0.7))
        })
    }

    private paintWater = (t: number) => {
        const span = SCENE.water[1] - SCENE.water[0] - 400
        for (const { dash: d, sprite } of this.water) {
            const appear = prog(t, SCENE.water[0] + d.d * span, SCENE.water[0] + d.d * span + 400)
            sprite.visible = appear > 0
            if (!sprite.visible) {
                continue
            }
            const x = d.x + d.amp * Math.sin(t / d.per + d.ph)
            const len = d.len * (0.85 + 0.15 * Math.sin(t / (d.per * 0.7) + d.ph * 2))
            setDash(sprite, x + len / 2, d.y, len, 0.9)
            sprite.alpha = d.a * appear
        }

        // The moon's path on the water: two glints a row, shimmering.
        const appear = smooth(prog(t, SCENE.moon[1] - 300, SCENE.moon[1] + 700))
        for (const { row, sprites } of this.glitter) {
            const cx = MOON.x + Math.sin(t / 700 + row.row * 0.9) * 2
            const alpha = appear * 0.55 * (1 - (row.y - HORIZON) / 190)
            sprites.forEach((sprite, j) => {
                sprite.visible = alpha > 0
                if (!sprite.visible) {
                    return
                }
                const off = (j ? 0.35 : -0.55) * row.hw + Math.sin(t / 380 + row.row * 1.7 + j * 2) * row.hw * 0.25
                const len = row.hw * (0.35 + 0.45 * Math.abs(Math.sin(t / 300 + row.row * 2.3 + j)))
                setDash(sprite, cx + off + len / 2, row.y, len, 1)
                sprite.alpha = alpha * (j ? 0.8 : 1)
            })
        }
    }

    private paintShip = (t: number) => {
        const showing = t >= Math.min(SCENE.shipInk[0], SCENE.rocks[0])
        this.shipLayer.sprite.visible = showing
        this.lanternGlow.visible = false
        this.reflections.forEach(r => (r.visible = false))
        if (!showing) {
            return
        }
        const pen = this.shipLayer.pen(true)
        if (!pen) {
            return
        }
        const st = paintShip(pen, t)
        this.shipLayer.commit()

        const lantern = lanternAt(st, t)
        if (lantern.lit <= 0) {
            return
        }
        const fl = lantern.flicker
        this.lanternGlow.visible = true
        this.lanternGlow.position.set(mirrorX(lantern.glow[0]), lantern.glow[1])
        this.lanternGlow.width = this.lanternGlow.height = 60 * st.s * (1 + 0.7 * lantern.flare)
        this.lanternGlow.alpha = (0.5 * fl + 0.4 * lantern.flare) * lantern.lit

        const near = st.s / 0.88
        this.reflections.forEach((sprite, i) => {
            const y = st.wl + 3 + i * 3.6 * near
            const hw = (3.4 - i * 0.5) * (0.75 + 0.35 * Math.sin(t / 140 + i * 1.9)) * near
            const x = lantern.glow[0] + Math.sin(t / 230 + i * 1.3) * 1.4
            sprite.visible = true
            setDash(sprite, x, y, hw * 2, 1.1)
            sprite.alpha = 0.42 * (1 - i / 5.5) * fl * lantern.lit
        })
    }
}
